import { balance } from '@game/gamedata';
import { findBody, findPlayer, type BodyState, type GameState, type PlayerState } from './state.js';

/** Um input de um jogador para um tick. */
export interface PlayerInput {
  playerId: number;
  seq: number;
  /** Direção desejada, -1..1. Normalizada aqui; NEVER confie no cliente. */
  moveX: number;
  moveZ: number;
  /** Bitmask de ações. */
  actions: number;
}

/** Segurar o campo magnético. Bit 0 de `actions`. */
export const ACTION_MAGNET = 1;

/** Cosseno do cone de auto-lock, calculado uma vez. Ver REV-02 sobre `Math.cos`. */
const CONE_COS = Math.cos((balance.magnet.coneDegrees * Math.PI) / 180);

/** Passo de tempo fixo. Derivado do tickRate, NEVER de tempo de parede. */
export const DT = 1 / balance.tickRate;

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/**
 * Direção do input já saneada. Reaproveitado entre jogadores DENTRO de um tick:
 * `step` processa um jogador por vez e NEVER guarda a referência.
 */
const move = { x: 0, z: 0 };

/**
 * Normaliza a direção de input e limita o módulo a 1, escrevendo em `move`.
 * É validação de anti-cheat: cliente pode mandar 9999.
 */
function sanitizeMove(moveX: number, moveZ: number): void {
  const x = Number.isFinite(moveX) ? clamp(moveX, -1, 1) : 0;
  const z = Number.isFinite(moveZ) ? clamp(moveZ, -1, 1) : 0;
  const lengthSquared = x * x + z * z;
  if (lengthSquared <= 1) {
    move.x = x;
    move.z = z;
    return;
  }
  const length = Math.sqrt(lengthSquared);
  move.x = x / length;
  move.z = z / length;
}

function approach(current: number, target: number, maxDelta: number): number {
  const delta = target - current;
  if (delta > maxDelta) return current + maxDelta;
  if (delta < -maxDelta) return current - maxDelta;
  return target;
}

/** Fator que traz `(x, z)` para dentro de `max`. 1 quando já cabe. */
function speedScale(x: number, z: number, max: number): number {
  const speed = Math.hypot(x, z);
  return speed > max ? max / speed : 1;
}

function integratePlayer(player: PlayerState, input: PlayerInput | undefined): void {
  const targetVx = move.x * balance.player.moveSpeed;
  const targetVz = move.z * balance.player.moveSpeed;

  const moving = move.x !== 0 || move.z !== 0;
  const rate = moving ? balance.player.accel : balance.player.friction;
  const maxDelta = rate * DT;

  player.vx = approach(player.vx, targetVx, maxDelta);
  player.vz = approach(player.vz, targetVz, maxDelta);

  // Arrasto do impulso pelo MÓDULO, e NEVER por eixo: por eixo, um impulso na
  // diagonal morreria antes de um impulso reto com a mesma velocidade — e
  // "meu arremesso na diagonal é pior" é o tipo de erro que ninguém diagnostica.
  const speed = Math.hypot(player.ix, player.iz);
  if (speed > 0) {
    const remaining = speed - balance.magnet.impulseDrag * DT;
    const scale = remaining <= 0 ? 0 : remaining / speed;
    player.ix *= scale;
    player.iz *= scale;
  }

  // O campo entra somado ao direcional: andar durante o puxão continua valendo.
  player.x += (player.vx + player.ix) * DT;
  player.z += (player.vz + player.iz) * DT;

  if (input !== undefined) player.lastSeq = input.seq;
}

/** Direção intencional do tick. Sem direcional, a última fica valendo. */
function updateFacing(player: PlayerState): void {
  const length = Math.hypot(move.x, move.z);
  if (length === 0) return;
  player.facingX = move.x / length;
  player.facingZ = move.z / length;
}

/**
 * Escolhe o melhor alvo dentro do alcance e do cone (`auto-lock` do PULSO).
 *
 * Chamada SÓ no tick em que o campo liga. Reescolher com o botão apertado faria
 * o campo pular de objeto sozinho no meio de uma jogada, que é exatamente a
 * queixa que `erro de mira não domina a experiência` manda evitar.
 *
 * Empate resolve pelo menor id, porque `state.bodies` está ordenado e a
 * comparação é estrita: a mesma entrada NEVER escolhe alvo diferente.
 */
function acquireTarget(state: GameState, player: PlayerState): number {
  const aimLength = Math.hypot(move.x, move.z);
  let best = 0;
  let bestDistance = Infinity;

  for (const body of state.bodies) {
    // Corpo na mão de outro jogador NEVER é alvo: disputa é fatia própria.
    if (body.heldBy !== 0 && body.heldBy !== player.id) continue;

    const dx = body.x - player.x;
    const dz = body.z - player.z;
    const distance = Math.hypot(dx, dz);
    if (distance > balance.magnet.range) continue;

    // Sem direcional o cone é o círculo inteiro: quem não apontou quis o mais perto.
    if (aimLength > 0 && distance > 0) {
      const cosine = ((dx * move.x) / aimLength + (dz * move.z) / aimLength) / distance;
      if (cosine < CONE_COS) continue;
    }
    if (distance < bestDistance) {
      bestDistance = distance;
      best = body.id;
    }
  }
  return best;
}

/** Soltar o botão: encerra o campo e transfere ou conserva o impulso. */
function releaseTarget(state: GameState, player: PlayerState): void {
  const body = findBody(state, player.magnetTargetId);
  player.magnetTargetId = 0;
  if (body === undefined) return;

  if (balance.bodies[body.kind].fixed) {
    // Contra o preso quem ganhou velocidade foi o robô.
    player.ix *= balance.magnet.releaseKeep;
    player.iz *= balance.magnet.releaseKeep;
    return;
  }
  if (body.heldBy === player.id) {
    // Capturado vira lançamento na direção preparada pelo direcional.
    body.heldBy = 0;
    body.vx = player.facingX * balance.magnet.launchSpeed;
    body.vz = player.facingZ * balance.magnet.launchSpeed;
    return;
  }
  // Soltar no meio do caminho: o objeto segue com o que já tinha.
  body.vx *= balance.magnet.releaseKeep;
  body.vz *= balance.magnet.releaseKeep;
}

/**
 * A regra do PULSO em uma função: contra alvo PRESO cede o robô, contra alvo
 * SOLTO cede o objeto. Quem decide é `fixed`.
 *
 * A promessa que estava escrita aqui — "objeto pesado é dado novo, e NEVER caminho
 * novo" — foi COBRADA quando ele entrou, e ela se pagou pela metade: o peso é dado
 * puro (`mass` divide a força, e nada aqui sabe o nome do tipo), mas a captura
 * precisou de uma pergunta a mais. Continua sendo dado quem responde — `capturable`
 * —, e a física continua sem um caminho por TIPO. É o mesmo formato do `fixed`.
 */
function applyMagnetForce(state: GameState, player: PlayerState): void {
  const body = findBody(state, player.magnetTargetId);
  if (body === undefined) {
    // Alvo sumiu do mundo entre um tick e outro: o campo cai junto.
    player.magnetTargetId = 0;
    return;
  }
  const kind = balance.bodies[body.kind];
  const dx = body.x - player.x;
  const dz = body.z - player.z;
  const distance = Math.hypot(dx, dz);
  if (distance === 0) return;
  const nx = dx / distance;
  const nz = dz / distance;

  if (kind.fixed) {
    const accel = balance.magnet.force / balance.magnet.playerMass;
    player.ix += nx * accel * DT;
    player.iz += nz * accel * DT;
    const scale = speedScale(player.ix, player.iz, balance.magnet.pullMaxSpeed);
    player.ix *= scale;
    player.iz *= scale;
    return;
  }

  if (body.heldBy === player.id) return;
  // Encostar só GRUDA o que o dado disser que gruda. O pesado do PULSO `move
  // lentamente ou exige campos combinados`, e capturá-lo o faria passar a andar na
  // velocidade do robô — o oposto exato do que ele existe para ser. Sem captura ele
  // continua recebendo força enquanto o campo estiver ligado, e é isso que faz dois
  // campos somarem em vez de um deles vencer a corrida até `captureDistance`.
  if (kind.capturable && distance <= balance.magnet.captureDistance) {
    body.heldBy = player.id;
    body.vx = 0;
    body.vz = 0;
    return;
  }
  // Massa divide a força: é ela que faz leve vir voando e pesado vir arrastando.
  const accel = balance.magnet.force / kind.mass;
  body.vx -= nx * accel * DT;
  body.vz -= nz * accel * DT;
  const scale = speedScale(body.vx, body.vz, balance.magnet.pullMaxSpeed);
  body.vx *= scale;
  body.vz *= scale;
}

/**
 * O PERIGO: enquanto o centro está dentro da zona, ela empurra para FORA.
 *
 * Radial a partir do centro da zona, e NEVER numa direção fixa: direção fixa
 * viraria esteira, e esteira NEVER é perigo — é geometria. Radial, a leitura é a
 * mesma de qualquer lado por onde se entre.
 *
 * O empurrão entra no canal do IMPULSO (`ix/iz`) no jogador, e NEVER na
 * velocidade do direcional: o direcional é zerado pela fricção a cada tick sem
 * input, e o empurrão morrendo junto faria a zona parar de existir para quem
 * solta o dedo — que é exatamente quem ela precisa alcançar.
 *
 * O TETO é o que decide o design (ver `hazard` em `gamedata`): abaixo de
 * `moveSpeed`, o robô atravessa andando; acima, a zona vira parede.
 *
 * Centro dentro, e NEVER o corpo inteiro: é a mesma regra da saída, e pela mesma
 * razão — é a que o jogador consegue ler enquanto joga.
 */
function applyHazardsToPlayer(state: GameState, player: PlayerState): void {
  for (const zone of state.hazards) {
    const dx = player.x - zone.x;
    const dz = player.z - zone.z;
    const distance = Math.hypot(dx, dz);
    if (distance > zone.radius) continue;
    // No centro EXATO NEVER existe direção para fora. Empurrar para um lado
    // escolhido aqui seria o sim inventando conteúdo; o tick seguinte, com o
    // jogador um fio de distância do centro, resolve sozinho.
    if (distance === 0) continue;
    player.ix += (dx / distance) * zone.push * DT;
    player.iz += (dz / distance) * zone.push * DT;
    const scale = speedScale(player.ix, player.iz, zone.maxSpeed);
    player.ix *= scale;
    player.iz *= scale;
  }
}

/**
 * O mesmo empurrão, no corpo SOLTO.
 *
 * O que está na mão de alguém NEVER é empurrado: quem carrega já está sendo
 * empurrado, e o objeto acompanha a mão (`integrateBody`). Empurrar os dois
 * seria contar o mesmo empurrão duas vezes, e o objeto na mão sairia do lugar
 * dela sem nada que explicasse.
 *
 * Corpo FIXO também NEVER: contra o que está preso ao cenário, nada cede.
 */
function applyHazardsToBody(state: GameState, body: BodyState): void {
  if (body.heldBy !== 0) return;
  if (balance.bodies[body.kind].fixed) return;
  for (const zone of state.hazards) {
    const dx = body.x - zone.x;
    const dz = body.z - zone.z;
    const distance = Math.hypot(dx, dz);
    if (distance > zone.radius || distance === 0) continue;
    body.vx += (dx / distance) * zone.push * DT;
    body.vz += (dz / distance) * zone.push * DT;
    const scale = speedScale(body.vx, body.vz, zone.maxSpeed);
    body.vx *= scale;
    body.vz *= scale;
  }
}

function integrateBody(state: GameState, body: BodyState): void {
  if (balance.bodies[body.kind].fixed) return;

  if (body.heldBy !== 0) {
    const holder = findPlayer(state, body.heldBy);
    if (holder !== undefined) {
      // Capturado acompanha o robô à frente dele: é a mão, e NEVER física.
      body.x = holder.x + holder.facingX * balance.magnet.holdDistance;
      body.z = holder.z + holder.facingZ * balance.magnet.holdDistance;
      body.vx = 0;
      body.vz = 0;
      return;
    }
    // Quem carregava saiu da sala. O objeto cai onde está.
    body.heldBy = 0;
  }

  const speed = Math.hypot(body.vx, body.vz);
  if (speed > 0) {
    const remaining = speed - balance.magnet.bodyDrag * DT;
    const scale = remaining <= 0 ? 0 : remaining / speed;
    body.vx *= scale;
    body.vz *= scale;
  }
  body.x += body.vx * DT;
  body.z += body.vz * DT;
}

/** Âncora é cenário: o robô para nela em vez de atravessar o próprio alvo. */
function collideWithFixedBodies(state: GameState, player: PlayerState): void {
  for (const body of state.bodies) {
    const kind = balance.bodies[body.kind];
    if (!kind.fixed) continue;

    const dx = player.x - body.x;
    const dz = player.z - body.z;
    const minDistance = kind.radius + balance.player.radius;
    const distanceSquared = dx * dx + dz * dz;
    if (distanceSquared >= minDistance * minDistance) continue;

    if (distanceSquared === 0) {
      player.x = body.x + minDistance;
      continue;
    }
    const distance = Math.sqrt(distanceSquared);
    const nx = dx / distance;
    const nz = dz / distance;
    player.x = body.x + nx * minDistance;
    player.z = body.z + nz * minDistance;

    // Mata só o que entra na âncora. As DUAS velocidades: o campo continua
    // puxando enquanto o botão estiver apertado, e sem isto o impulso cresceria
    // até o teto encostado no alvo, para explodir no instante do release.
    const radialInput = player.vx * nx + player.vz * nz;
    if (radialInput < 0) {
      player.vx -= radialInput * nx;
      player.vz -= radialInput * nz;
    }
    const radialImpulse = player.ix * nx + player.iz * nz;
    if (radialImpulse < 0) {
      player.ix -= radialImpulse * nx;
      player.iz -= radialImpulse * nz;
    }
  }
}

/** Mantém um corpo solto dentro da arena, com a mesma regra do jogador. */
function constrainBodyToArena(body: BodyState): void {
  const limit = balance.arena.radius - balance.bodies[body.kind].radius;
  const distanceSquared = body.x * body.x + body.z * body.z;
  if (distanceSquared <= limit * limit) return;

  const distance = Math.sqrt(distanceSquared);
  if (distance === 0) return;
  const nx = body.x / distance;
  const nz = body.z / distance;
  body.x = nx * limit;
  body.z = nz * limit;

  const radial = body.vx * nx + body.vz * nz;
  if (radial > 0) {
    body.vx -= radial * nx;
    body.vz -= radial * nz;
  }
}

/** Mantém o jogador dentro da arena, matando a velocidade radial na borda. */
function constrainToArena(player: PlayerState): void {
  const limit = balance.arena.radius - balance.player.radius;
  const distanceSquared = player.x * player.x + player.z * player.z;
  if (distanceSquared <= limit * limit) return;

  const distance = Math.sqrt(distanceSquared);
  if (distance === 0) return;
  const nx = player.x / distance;
  const nz = player.z / distance;
  player.x = nx * limit;
  player.z = nz * limit;

  const radial = player.vx * nx + player.vz * nz;
  if (radial > 0) {
    player.vx -= radial * nx;
    player.vz -= radial * nz;
  }
}

/**
 * Separa jogadores sobrepostos. Percorre pares em ordem de índice,
 * e o array está ordenado por id: o resultado NEVER depende de ordem de chegada.
 */
function separatePlayers(state: GameState): void {
  const minDistance = balance.player.radius * 2;
  const players = state.players;
  for (let i = 0; i < players.length; i++) {
    const a = players[i];
    if (a === undefined) continue;
    for (let j = i + 1; j < players.length; j++) {
      const b = players[j];
      if (b === undefined) continue;

      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const distanceSquared = dx * dx + dz * dz;
      if (distanceSquared >= minDistance * minDistance) continue;

      if (distanceSquared === 0) {
        // Sobreposição exata: desloca de forma determinística pelo id.
        a.x -= minDistance / 2;
        b.x += minDistance / 2;
        continue;
      }
      const distance = Math.sqrt(distanceSquared);
      const push = (minDistance - distance) / 2;
      const nx = dx / distance;
      const nz = dz / distance;
      a.x -= nx * push;
      a.z -= nz * push;
      b.x += nx * push;
      b.z += nz * push;
    }
  }
}

/**
 * Avança a simulação exatamente um tick.
 *
 * Muta `state` de propósito: alocar estado novo a 30 Hz gera lixo e
 * pressiona o GC no celular (ver BUD-js-frame). Use cloneState para rollback.
 *
 * `inputs` ALWAYS ordenado por playerId — o chamador garante.
 */
export function step(state: GameState, inputs: readonly PlayerInput[]): void {
  for (const player of state.players) {
    let input: PlayerInput | undefined;
    for (const candidate of inputs) {
      if (candidate.playerId === player.id) {
        input = candidate;
        break;
      }
    }
    sanitizeMove(input?.moveX ?? 0, input?.moveZ ?? 0);

    // A direção vem ANTES do campo de propósito: soltar e virar o direcional
    // acontecem no MESMO input, e lançar para onde o dedo apontava no tick
    // passado seria o objeto saindo torto sem explicação nenhuma.
    updateFacing(player);

    // Só um input NOVO muda o estado do botão. Tick sem input mantém o que
    // estava — ausência de pacote NEVER é a informação "soltei" (ver `magnetHeld`).
    if (input !== undefined) player.magnetHeld = (input.actions & ACTION_MAGNET) !== 0;
    const wantsMagnet = player.magnetHeld;
    if (!wantsMagnet) {
      if (player.magnetTargetId !== 0) releaseTarget(state, player);
    } else {
      if (player.magnetTargetId === 0) player.magnetTargetId = acquireTarget(state, player);
      if (player.magnetTargetId !== 0) applyMagnetForce(state, player);
    }

    // Depois do campo e ANTES de integrar: o empurrão é do tick em que o robô
    // está dentro, e NEVER do seguinte. Somado ao campo de propósito — puxar-se
    // para fora do perigo com o ímã tem que valer mais do que só andar.
    applyHazardsToPlayer(state, player);

    integratePlayer(player, input);
  }

  for (const body of state.bodies) {
    applyHazardsToBody(state, body);
    integrateBody(state, body);
  }

  separatePlayers(state);
  for (const player of state.players) {
    constrainToArena(player);
    collideWithFixedBodies(state, player);
    // De novo: sair de dentro da âncora pode ter jogado o robô para fora da borda.
    constrainToArena(player);
  }
  for (const body of state.bodies) constrainBodyToArena(body);

  state.tick += 1;
}

/** Aplica um input só se o jogador existir. Usado pelo servidor. */
export function inputForPlayer(
  state: GameState,
  input: PlayerInput,
): PlayerInput | undefined {
  return findPlayer(state, input.playerId) === undefined ? undefined : input;
}
